// File: src/pages/CreateCostCard.js

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import FormulaBuilderModal from '../components/FormulaBuilderModal'
import './CostCards.css'
import { FaWrench } from 'react-icons/fa';

// ─── MathJS setup ───────────────────────────────────────────────────────────
import { create, all } from 'mathjs'
// create a fresh instance
const math = create(all)
// register a user‐friendly `if(cond, trueVal, falseVal)` helper
math.import({
  if: function(condition, trueVal, falseVal) {
    return condition ? trueVal : falseVal
  }
}, { override: true })
// ──────────────────────────────────────────────────────────────────────────────

// month/year pickers
const MONTHS = [
  { value: '',   label: 'Month'     },
  { value: '01', label: 'January'   },
  { value: '02', label: 'February'  },
  { value: '03', label: 'March'     },
  { value: '04', label: 'April'     },
  { value: '05', label: 'May'       },
  { value: '06', label: 'June'      },
  { value: '07', label: 'July'      },
  { value: '08', label: 'August'    },
  { value: '09', label: 'September' },
  { value: '10', label: 'October'   },
  { value: '11', label: 'November'  },
  { value: '12', label: 'December'  }
]

const YEARS = [''].concat(
  Array.from({ length: 2060 - 2000 + 1 }, (_, i) => (2000 + i).toString())
)

export default function CreateCostCard() {
  const { templateKey }  = useParams()
  const location         = useLocation()
  const { state }        = location
  const existingFromNav  = state?.card || null
  const navigate         = useNavigate()

  // If you have set REACT_APP_API_URL, point at its /api, otherwise proxy at /api
  const API_BASE = process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`
    : '/api'

  // Determine whether we're using built-in or custom
  const effectiveKey = (
    templateKey && templateKey !== 'undefined' && templateKey !== 'new'
      ? templateKey
      : existingFromNav?.templateKey
  ) || 'import'

  const editIdFromQuery = new URLSearchParams(location.search).get('id')

  const [template, setTemplate]             = useState(null)
  const [card, setCard]                     = useState({})
  const [computed, setComputed]             = useState({})
  const [formulas, setFormulas]             = useState({})
  const [editingFormula, setEditingFormula] = useState(null)
  const [editingDropdownField, setEditingDropdownField] = useState(null);
  const [dropdownOptionEdits, setDropdownOptionEdits] = useState([]);
  const [editingDropdownOptionIdx, setEditingDropdownOptionIdx] = useState(null);
  // Local overrides for dropdown option formulas: { [fieldKey]: { [optionValue]: formula } }
  const [localDropdownOptionOverrides, setLocalDropdownOptionOverrides] = useState({});
  // Local-only actuals entered by user; does not affect formulas
  const [actuals, setActuals] = useState({});
  // Variances returned from backend after persisting
  const [serverVariances, setServerVariances] = useState({});

  // Resolve existing card either from navigation state or by fetching via ?id= query
  const [existing, setExisting] = useState(existingFromNav)
  const [loadingExisting, setLoadingExisting] = useState(!!editIdFromQuery)

  useEffect(() => {
    if (!editIdFromQuery) {
      setLoadingExisting(false)
      return undefined
    }

    let cancelled = false
    setLoadingExisting(true)

    ;(async () => {
      try {
        const res = await fetch(`${API_BASE}/costcards/${encodeURIComponent(editIdFromQuery)}`)
        if (res.ok && !cancelled) {
          setExisting(await res.json())
        }
      } catch {
        // keep navigation state fallback when fetch fails
      } finally {
        if (!cancelled) setLoadingExisting(false)
      }
    })()

    return () => { cancelled = true }
  }, [editIdFromQuery, API_BASE])

  // Helpers to format saved dates for HTML inputs
  const formatDateForInput = (v) => {
    if (!v) return ''
    try {
      if (v instanceof Date) return v.toISOString().slice(0,10)
      const s = String(v)
      
      // Handle full ISO date strings (with time)
      const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/)
      if (isoMatch) return isoMatch[1]
      
      // Handle MM-YYYY format - return empty string (no default to first day)
      const monthYearMatch = s.match(/^(\d{2})-(\d{4})$/)
      if (monthYearMatch) {
        return '' // Don't default to first day of month
      }
      
      // Handle YYYY-MM format - return empty string (no default to first day)
      const yearMonthMatch = s.match(/^(\d{4})-(\d{2})$/)
      if (yearMonthMatch) {
        return '' // Don't default to first day of month
      }
      
      // Try native parse
      const d = new Date(s)
      if (!isNaN(d)) return d.toISOString().slice(0,10)
    } catch {}
    return ''
  }
  const formatMonthForInput = (v) => {
    if (!v) return ''
    try {
      if (v instanceof Date) return `${v.getUTCFullYear()}-${String(v.getUTCMonth()+1).padStart(2,'0')}`
      const s = String(v)
      let m = s.match(/^(\d{2})[-\/](\d{4})$/)
      if (m) return `${m[2]}-${m[1]}`
      m = s.match(/^(\d{4})[-\/](\d{2})$/)
      if (m) return `${m[1]}-${m[2]}`
      const d = new Date(s)
      if (!isNaN(d)) return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,'0')}`
    } catch {}
    return ''
  }

  // Handler to open dropdown formula modal
  function openDropdownFormulaModal(field) {
    // Use overrides if present, otherwise template options
    const overrides = localDropdownOptionOverrides[field.key] || {};
    setEditingDropdownField(field.key);
    setDropdownOptionEdits(
      field.options.map(opt => ({
        ...opt,
        formula: overrides[opt.value] !== undefined ? overrides[opt.value] : opt.formula || ''
      }))
    );
    setEditingDropdownOptionIdx(null);
  }

  // Handler to save dropdown option formulas (local override only)
  function saveDropdownOptionFormulas() {
    setLocalDropdownOptionOverrides(prev => {
      const newOverrides = { ...prev };
      newOverrides[editingDropdownField] = {};
      dropdownOptionEdits.forEach(opt => {
        newOverrides[editingDropdownField][opt.value] = opt.formula || '';
      });
      return newOverrides;
    });
    setEditingDropdownField(null);
    setDropdownOptionEdits([]);
    setEditingDropdownOptionIdx(null);
  }

  // Handler to open FormulaBuilderModal for a specific dropdown option
  function openOptionFormulaBuilder(idx) {
    setEditingDropdownOptionIdx(idx);
  }

  // Handler to save formula from FormulaBuilderModal for a dropdown option
  function saveOptionFormula(expr) {
    setDropdownOptionEdits(edits => {
      const newEdits = [...edits];
      newEdits[editingDropdownOptionIdx] = {
        ...newEdits[editingDropdownOptionIdx],
        formula: expr
      };
      return newEdits;
    });
    setEditingDropdownOptionIdx(null);
  }

  // 1️⃣ Load the template (JSON or custom from backend)
  useEffect(() => {
    async function loadTemplate() {
      try {
        let tmpl

        if (effectiveKey.startsWith('custom:')) {
          // custom template fetch
          const id  = effectiveKey.split(':')[1]
          const res = await fetch(
            `${API_BASE}/custom-templates/${encodeURIComponent(id)}`
          )
          if (!res.ok) {
            const txt = await res.text().catch(() => res.statusText)
            throw new Error(txt || `Could not load custom template "${id}"`)
          }
          const data = await res.json()
          tmpl = {
            key:      effectiveKey,
            label:    data.name,
            settings: data.settings,
            fields:   data.fields
          }
        } else {
          // built-in JSON
          const mod = await import(`../data/templates/${effectiveKey}.json`)
          tmpl = mod.default || mod
        }

        setTemplate(tmpl)

        // initialize card + formulas
        const initialRaw = {}
        const initialFrm = {}
        tmpl.fields.forEach(f => {
          initialRaw[f.key] = (f.type === 'select' || f.type === 'dropdown')
            ? f.options?.[0]?.value ?? ''
            : ''
          if (f.type === 'computed') {
            initialFrm[f.key] = f.formula || ''
          }
        })
        if (existing?.formulaFields?.length) {
          existing.formulaFields.forEach(ff => {
            initialFrm[ff.key] = ff.expression
          })
        }

        // Hydrate template date/period field from periodExact for existing cards
        let nextCard
        if (existing) {
          nextCard = { ...initialRaw, ...existing }
          console.log('Loading existing card data:', existing)
          try {
            const dateField = tmpl.fields.find(f => f.type === 'date' || f.type === 'period')
            if (dateField && (nextCard[dateField.key] == null || nextCard[dateField.key] === '') && existing.periodExact) {
              nextCard[dateField.key] = existing.periodExact
              console.log('Hydrated date field:', dateField.key, 'with value:', existing.periodExact)
            }
          } catch {}
        } else {
          nextCard = initialRaw
        }

        setCard(nextCard)
        setFormulas(initialFrm)
        
        // Initialize actuals from backend data when editing existing card
        console.log('CreateCostCard - existing card data:', existing)
        console.log('CreateCostCard - actuals from existing:', existing?.actuals)
        if (existing?.actuals) {
          const actualsFromBackend = {}
          Object.entries(existing.actuals).forEach(([fieldKey, actualData]) => {
            console.log(`CreateCostCard - processing actual for ${fieldKey}:`, actualData)
            if (actualData && actualData.amount != null) {
              actualsFromBackend[fieldKey] = actualData.amount
            }
          })
          console.log('CreateCostCard - final actualsFromBackend:', actualsFromBackend)
          setActuals(actualsFromBackend)
        }
        
        // Initialize server variances from backend data when editing existing card
        if (existing?.variances) {
          setServerVariances(existing.variances)
        }

      } catch (err) {
        console.error('Template load error:', err)
        setTemplate({ error: err.message })
      }
    }
    loadTemplate()
  }, [effectiveKey, existing, API_BASE])

  // 2️⃣ Re-compute any computed/custom fields
  useEffect(() => {
    if (!template || !Array.isArray(template.fields)) return

    // build our evaluation scope
    let scope = {}
    // 1. Fill scope with all raw values (number, text, etc.) and selected dropdown values
    template.fields.forEach(f => {
      if (['number', 'computed'].includes(f.type)) {
        scope[f.key] = Number(card[f.key]) || 0;
      } else if (f.type === 'dropdown' || f.type === 'select') {
        scope[f.key] = card[f.key]; // temporarily set to value, will update below
      } else {
        scope[f.key] = card[f.key] ?? '';
      }
    });
    // 2. True fixpoint: for each pass, update all dropdowns and computed fields using latest values
    let changed = true, pass = 0;
    while (changed && pass < 15) {
      changed = false;
      // Dropdowns
      template.fields.forEach(f => {
        if (f.type === 'dropdown' || f.type === 'select') {
          const selected = f.options?.find(opt => opt.value === card[f.key]);
          // Use local override if present
          const overrideFormula = localDropdownOptionOverrides?.[f.key]?.[selected?.value];
          const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula;
          if (selected && formulaToUse && formulaToUse.trim()) {
            try {
              // Coerce referenced fields to numbers where possible
              const evalScope = { ...scope };
              Object.keys(evalScope).forEach(k => {
                if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
              });
              const prev = scope[f.key];
              const computed = math.evaluate(formulaToUse, evalScope);
              scope[f.key] = computed;
              if (scope[f.key] !== prev) changed = true;
            } catch (e) {
              const prev = scope[f.key];
              scope[f.key] = 0;
              if (scope[f.key] !== prev) changed = true;
            }
          } else if (selected && !isNaN(Number(selected.value))) {
            const prev = scope[f.key];
            scope[f.key] = Number(selected.value);
            if (scope[f.key] !== prev) changed = true;
          } else {
            // fallback: use the value as is
            const prev = scope[f.key];
            scope[f.key] = card[f.key];
            if (scope[f.key] !== prev) changed = true;
          }
        }
      });
      // Computed fields
      template.fields.forEach(f => {
        if (f.type === 'computed' && f.formula && f.formula.trim()) {
          try {
            const evalScope = { ...scope };
            Object.keys(evalScope).forEach(k => {
              if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
            });
            const prev = scope[f.key];
            scope[f.key] = math.evaluate(f.formula, evalScope);
            if (scope[f.key] !== prev) changed = true;
          } catch (e) {
            const prev = scope[f.key];
            scope[f.key] = 0;
            if (scope[f.key] !== prev) changed = true;
          }
        }
      });
      pass++;
    }

    const curr = card.currency || template.settings?.currency || ''
    const out  = {}

    template.fields.forEach(f => {
      const built  = f.type === 'computed' && (f.formula || '').trim()
      const custom = (formulas[f.key] || '').trim()
      if (built || custom) {
        // substitute placeholders with either literals or scope values
        let expr = (custom || built)
          // If formulas reference {currency}, prefer numeric scope value; fall back to 0
          .replace(/\{currency\}/g, String(scope.currency ?? 0))
          .replace(/\{freightType\}/g, card.freightType || '')
          .replace(/\{(\w+)\}/g, (_, k) => scope[k])

        let v = 0
        try {
          // use our mathjs instance
          v = math.evaluate(expr, scope)
        } catch {
          v = 0
        }
        if (Number.isNaN(v)) v = 0
        out[f.key]   = v
        scope[f.key] = out[f.key]
      }
    })

    setComputed(out)
  }, [card, formulas, template, localDropdownOptionOverrides])

  // 3️⃣ Handlers
  const handleChange     = (key, val) => setCard(c => ({ ...c, [key]: val }))
  const startEditFormula = key         => setEditingFormula(key)

  // —— UPDATED saveFormula —— strips {…} before parse!
  const saveFormula = expr => {
    // remove braces for syntax check
    const clean = expr.replace(/\{(\w+)\}/g, (_, k) => k)
    try {
      math.parse(clean)  // validate on our math instance
      // store the original (with {…})
      setFormulas(f => ({ ...f, [editingFormula]: expr }))
      setEditingFormula(null)
    } catch (err) {
      alert('Formula syntax error: ' + err.message)
    }
  }

  // 4️⃣ Submit
  const handleSubmit = async e => {
    e.preventDefault()
    if (!template) return

    const formulaFields = Object.entries(formulas)
      .filter(([, ex]) => ex.trim())
      .map(([key, ex]) => {
        const def = template.fields.find(f => f.key === key)
        return { key, label: def?.label || key, expression: ex }
      })

    const payload = {
      templateKey: effectiveKey,
      ...card,
      ...computed,
      formulaFields,
      // include current actuals so a Save persists new edits even if onBlur didn’t fire
      actuals: Object.fromEntries(
        Object.entries(actuals).map(([k, v]) => [k, { amount: Number(String(v).replace(/,/g,'')) || 0, currency: card.currency || null }])
      )
    }

    // Ensure we always send a normalized exact date even for legacy/variant keys
    const extractDateCandidate = () => {
      // Prefer the actual date/period field defined in the current template
      const templateDateKey = template?.fields?.find(
        f => f.type === 'date' || f.type === 'period'
      )?.key
      if (templateDateKey && card[templateDateKey]) return card[templateDateKey]
      // Otherwise fall back to common legacy keys
      const candidates = [
        // Intentionally check 'period' before any stale 'periodExact' when template doesn't define it
        card.period,
        card.date,
        card.Date,
        card.period_date,
        card.periodDate,
        card.period_exact,
        card.periodExact
      ]
      return candidates.find(Boolean)
    }
    const normalizeDate = (v) => {
      if (!v) return null
      // Accept Date object, timestamps, or strings in common formats
      if (v instanceof Date || typeof v === 'number') {
        const d = new Date(v)
        if (isNaN(d)) return null
        return d.toISOString().slice(0,10)
      }
      const s = String(v)
      // YYYY-MM-DD or YYYY/MM/DD
      let m = s.match(/^(\d{4})[\/-](\d{2})[\/-](\d{2})$/)
      if (m) return `${m[1]}-${m[2]}-${m[3]}`
      // DD-MM-YYYY or DD/MM/YYYY
      m = s.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/)
      if (m) return `${m[3]}-${m[2]}-${m[1]}`
      // MM-YYYY or YYYY-MM → pick first day of month
      m = s.match(/^(\d{2})-(\d{4})$/)
      if (m) return `${m[2]}-${m[1]}-01`
      m = s.match(/^(\d{4})-(\d{2})$/)
      if (m) return `${m[1]}-${m[2]}-01`
      // Fallback to native parse
      const d = new Date(s)
      if (!isNaN(d)) return d.toISOString().slice(0,10)
      return null
    }
    const candidate = extractDateCandidate()
    const normalized = normalizeDate(candidate)
    if (normalized) payload.periodExact = normalized

    // Ensure exchangeRate is persisted: if missing, derive from currency dropdown value
    try {
      const fCurrency = template.fields.find(ff => ff.key === 'currency' || /currency/i.test(ff.label || ff.key))
      if (fCurrency) {
        const selected = fCurrency.options?.find(opt => opt.value === card[fCurrency.key])
        let derived = null
        if (selected) {
          const overrideFormula = localDropdownOptionOverrides?.[fCurrency.key]?.[selected?.value]
          const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula
          if (formulaToUse && formulaToUse.trim()) {
            const scope = { ...card, ...computed }
            Object.keys(scope).forEach(k => { if (!isNaN(Number(scope[k]))) scope[k] = Number(scope[k]) })
            try { derived = Number(math.evaluate(formulaToUse, scope)) } catch { derived = null }
          } else if (!isNaN(Number(selected?.value))) {
            derived = Number(selected.value)
          }
        }
        if (payload.exchangeRate == null && derived != null && isFinite(derived)) {
          payload.exchangeRate = derived
        }
      }
    } catch {}

    const url    = existing
      ? `${API_BASE}/costcards/${existing._id}`
      : `${API_BASE}/costcards`
    const method = existing ? 'PUT' : 'POST'

    console.log('CreateCostCard submit payload:', payload)
    console.log('Date fields in payload:', {
      periodExact: payload.periodExact,
      period: payload.period,
      date: payload.date
    })

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type':'application/json' },
      body:    JSON.stringify(payload)
    })
    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText)
      return alert(txt || `Save failed (${res.status})`)
    }

    navigate('/costcards', { replace:true })
  }

  // 5️⃣ Render
  if (!template || loadingExisting) {
    return (
      <div className="cost-cards-page">
        <p>{loadingExisting ? 'Loading cost card…' : 'Loading template…'}</p>
      </div>
    )
  }
  if (template.error) {
    return (
      <div className="cost-cards-page error">
        <p>{template.error}</p>
      </div>
    )
  }
  if (!Array.isArray(template.fields)) {
    return (
      <div className="cost-cards-page error">
        <p>Template is missing its <code>fields</code> array.</p>
      </div>
    )
  }

  // conditional freight fields
  const hasFT = template.fields.some(f => f.key === 'freightType')
  const freightKeys = [
    'totalFreight',
    'totalFreightIrelandMsaEuro',
    'totalFreightIrelandIcdKes',
    'totalFreightChinaMsaUsd'
  ]
  const visibleFields = hasFT
    ? template.fields.filter(f => {
        if (f.key === 'freightType') return true
        if (freightKeys.includes(f.key)) {
          return card.freightType === f.key
        }
        return true
      })
    : template.fields

  const curr = card.currency || template.settings?.currency || ''

  const fmtNum = v => {
    const n = Number(v)
    if (!isFinite(n)) return ''
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  }

  const computeVariance = (planned, actual) => {
    const p = Number(planned)
    const a = Number(actual)
    if (!isFinite(p) || !isFinite(a)) return null
    return Number((a - p).toFixed(2))
  }

  // Persist an actual value to backend (only when editing an existing card)
  const persistActual = async (fieldKey, amount) => {
    console.log('CreateCostCard - persistActual called:', { fieldKey, amount, existing: !!existing, cardId: existing?._id || card?._id })
    try {
      const params = new URLSearchParams(location.search)
      const idFromQuery = params.get('id')
      const cardId = idFromQuery || existing?._id || card?._id
      if (!cardId) {
        console.log('CreateCostCard - persistActual: No cardId, skipping')
        return // not persisted for new cards
      }
      // API_BASE already includes "/api"; avoid double "/api/api"
      const url = `${API_BASE}/costcards/${encodeURIComponent(cardId)}/actuals/${encodeURIComponent(fieldKey)}`
      console.log('CreateCostCard - persistActual: Making PATCH request to:', url)
      const token = localStorage.getItem('token')
      const normalized = typeof amount === 'string' ? amount.replace(/,/g, '') : amount
      const numericAmount = Number(normalized)
      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ amount: isFinite(numericAmount) ? numericAmount : 0, currency: card.currency || null })
      })
      console.log('CreateCostCard - persistActual: Response status:', res.status)
      if (!res.ok) {
        const txt = await res.text().catch(() => res.statusText)
        console.log('CreateCostCard - persistActual: Response not ok:', res.status, res.statusText, txt)
        alert(`Failed to save actual for ${fieldKey}: ${res.status} ${res.statusText}`)
        return
      }
      const data = await res.json()
      console.log('CreateCostCard - persistActual: Response data:', data)
      setServerVariances(v => ({ ...v, [fieldKey]: data.variance }))
    } catch (error) {
      console.error('CreateCostCard - persistActual: Error:', error)
      alert(`Error saving actual for ${fieldKey}. Check console for details.`)
    }
  }

  const isExistingCard = Boolean(existing?._id)

  return (
    <div className={`cost-cards-page ${!isExistingCard ? 'cc-two-col' : ''}`}>
      <h1>{existing ? 'Edit' : 'Create'} {template?.label || 'Cost Card'}</h1>
      {/* Test Actuals button removed */}
      <form onSubmit={handleSubmit} className="cost-card-form">
        {visibleFields.map(f => {
          // Patch: treat text fields with options as dropdowns
          const effectiveType = (f.type === 'text' && Array.isArray(f.options) && f.options.length > 0) ? 'dropdown' : f.type;
          const label      = (f.label || f.key).replace(/\{currency\}/g, curr)
          const isCustom   = Boolean(formulas[f.key]?.trim())
          const isComputed = effectiveType === 'computed' || isCustom
          const raw        = isComputed
            ? (computed[f.key] ?? 0)
            : (card[f.key] ?? '')
          const [mo, yr]   = String(card[f.key] ?? '-').split('-')

          // Dropdown/select computed value display
          let dropdownComputedValue = '';
          if (effectiveType === 'dropdown' || effectiveType === 'select') {
            const selected = f.options?.find(opt => opt.value === card[f.key]);
            if (selected) {
              // Prefer local override formula if present
              const overrideFormula = localDropdownOptionOverrides?.[f.key]?.[selected?.value];
              const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula;
              if (formulaToUse && formulaToUse.trim()) {
                try {
                  const evalScope = { ...card, ...computed };
                  Object.keys(evalScope).forEach(k => {
                    if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
                  });
                  dropdownComputedValue = math.evaluate(formulaToUse, evalScope);
                } catch {
                  dropdownComputedValue = 0;
                }
              } else if (!isNaN(Number(selected.value))) {
                dropdownComputedValue = Number(selected.value);
              } else {
                dropdownComputedValue = selected.value;
              }
            }
          }

          // Render period/date as a single side-by-side line (no Actual/Variance)
          if (f.key === 'periodExact' || f.key === 'period') {
            // Prioritize periodExact over the template's period field
            const dateValue = card.periodExact || card[f.key] || ''
            const formattedDate = formatDateForInput(dateValue)
            console.log('Date input rendering:', {
              fieldKey: f.key,
              rawValue: dateValue,
              formattedValue: formattedDate,
              cardPeriodExact: card.periodExact,
              cardPeriod: card[f.key]
            })
            return (
              <div className="cc-line" key={f.key}>
                <div className="cc-name">
                  {label}
                  <button
                    type="button"
                    className="formula-btn"
                    onClick={() => startEditFormula(f.key)}
                    title="Edit custom formula"
                    style={{ marginLeft: 8 }}
                  >⚙️</button>
                </div>
                <div className="cc-cell">
                  <input
                    type="date"
                    value={formattedDate}
                    onChange={e => {
                      console.log('Date input changed:', e.target.value)
                      setCard(c => ({ ...c, periodExact: e.target.value, [f.key]: e.target.value }))
                    }}
                    disabled={isComputed}
                    className="cc-readonly"
                    placeholder="Select date"
                  />
                </div>
                <div className="cc-cell"></div>
                <div className="cc-cell"></div>
              </div>
            )
          }

          // Always render dropdown/select as dropdown
          if (effectiveType === 'dropdown' || effectiveType === 'select') {
            // Dropdown/select computed value display
            let dropdownComputedValue = '';
            const selected = f.options?.find(opt => opt.value === card[f.key]);
            if (selected) {
              const overrideFormula = localDropdownOptionOverrides?.[f.key]?.[selected?.value];
              const formulaToUse = overrideFormula !== undefined ? overrideFormula : selected?.formula;
              if (formulaToUse && formulaToUse.trim()) {
                try {
                  const evalScope = { ...card, ...computed };
                  Object.keys(evalScope).forEach(k => {
                    if (!isNaN(Number(evalScope[k]))) evalScope[k] = Number(evalScope[k]);
                  });
                  dropdownComputedValue = math.evaluate(formulaToUse, evalScope);
                } catch {
                  dropdownComputedValue = 0;
                }
              } else if (!isNaN(Number(selected.value))) {
                dropdownComputedValue = Number(selected.value);
              } else {
                dropdownComputedValue = selected.value;
              }
            }
            // Show formula-btn if any option has a formula
            const hasDropdownFormulas = Array.isArray(f.options) && f.options.some(opt => opt.formula && opt.formula.trim());
            const isExistingCard = Boolean(existing?._id)
            return (
              <div className="cc-line" key={f.key}>
                <div className="cc-name">{label}
                  {hasDropdownFormulas && (
                    <button
                      type="button"
                      className="formula-btn"
                      onClick={() => openDropdownFormulaModal(f)}
                      title="Edit dropdown option formulas"
                      style={{ marginLeft: 8 }}
                    >⚙️</button>
                  )}
                </div>
                <div className="cc-cell">
                  <div className="cc-select-value">
                    <select
                      value={card[f.key] || ''}
                      onChange={e => handleChange(f.key, e.target.value)}
                      disabled={isComputed}
                    >
                      <option value="">Select…</option>
                      {Array.isArray(f.options) && f.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="cc-select-number">{fmtNum(dropdownComputedValue)}</span>
                  </div>
                </div>
                {isExistingCard ? (
                  <>
                    <div className="cc-cell">
                      <input
                        type="number"
                        value={actuals[f.key] ?? ''}
                        onChange={e => setActuals(a => ({ ...a, [f.key]: e.target.value }))}
                        onBlur={e => {
                          console.log('CreateCostCard - onBlur triggered for field:', f.key, 'value:', e.target.value)
                          persistActual(f.key, e.target.value)
                        }}
                        className="cc-input-right"
                        placeholder="What Was Actually Charged"
                      />
                    </div>
                    <div className="cc-cell">
                      {(() => {
                        const planned = Number(dropdownComputedValue) || 0
                        const actual  = actuals[f.key]
                        const v = (serverVariances[f.key] != null) ? serverVariances[f.key] : computeVariance(planned, actual)
                        const sign = v == null ? '' : (v > 0 ? '+' : '')
                        const cls = v == null || v === 0 ? 'text-gray-600' : (v > 0 ? 'text-red-600' : 'text-green-600')
                        const val = v == null ? 'Variance' : `${sign}${fmtNum(v)}`
                        const centerClass = v == null ? 'center' : ''
                        return <input className={`cc-readonly variance-cell ${centerClass} ${cls}`} readOnly value={val} />
                      })()}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="cc-cell"></div>
                    <div className="cc-cell"></div>
                  </>
                )}
              </div>
            );
          }

          // Fallback for unknown types
          if (!["text","number","computed","month"].includes(effectiveType)) {
            return (
              <div className="form-group" key={f.key}>
                <label>{label}</label>
                <div style={{ color: 'red' }}>Unknown field type: {f.type}</div>
              </div>
            );
          }

          const labelLower = String(label).toLowerCase()
          const keyLower   = String(f.key || '').toLowerCase()
          const noActualVariance = (
            ['supplier', 'proforma', 'product name', 'hs code'].some(s => labelLower.includes(s)) ||
            ['suppliername','proformainvoiceno','productname','hscode'].some(s => keyLower.includes(s))
          )
          const isExistingCard = Boolean(existing?._id)
          const showActualVariance = isExistingCard && !noActualVariance && (effectiveType === 'number' || effectiveType === 'computed')

          return (
            <div className="cc-line" key={f.key}>
              {/* Name */}
              <div className="cc-name">
                {label}
                <button
                  type="button"
                  className="formula-btn"
                  onClick={() => startEditFormula(f.key)}
                  title="Edit custom formula"
                  style={{ marginLeft: 8 }}
                >⚙️</button>
              </div>

              {/* Planned (readonly if computed) */}
              <div className="cc-cell">
                {(() => {
                  const inputType = isComputed ? 'text' : (effectiveType === 'number' ? 'number' : 'text')
                  const displayValue = isComputed ? fmtNum(raw) : raw
                  return (
                    <input
                      type={inputType}
                      value={displayValue}
                      onChange={e => handleChange(f.key, e.target.value)}
                      readOnly={isComputed}
                      className={isComputed ? 'cc-readonly' : 'cc-input-right'}
                    />
                  )
                })()}
              </div>

              {/* Actual */}
              <div className="cc-cell">
                {showActualVariance ? (
                  <input
                    type="number"
                    value={actuals[f.key] ?? ''}
                    onChange={e => setActuals(a => ({ ...a, [f.key]: e.target.value }))}
                    onBlur={e => {
                      console.log('CreateCostCard - onBlur triggered for field (second input):', f.key, 'value:', e.target.value)
                      persistActual(f.key, e.target.value)
                    }}
                    className="cc-input-right"
                    placeholder="What Was Actually Charged"
                  />
                ) : null}
              </div>

              {/* Variance */}
              <div className="cc-cell">
                {showActualVariance ? (() => {
                  if (!(effectiveType === 'number' || effectiveType === 'computed')) return null
                  const planned = isComputed ? (computed[f.key] ?? 0) : Number(card[f.key])
                  const actual  = actuals[f.key]
                  const v = (serverVariances[f.key] != null) ? serverVariances[f.key] : computeVariance(planned, actual)
                  const sign = v == null ? '' : (v > 0 ? '+' : '')
                  const cls = v == null || v === 0 ? 'text-gray-600' : (v > 0 ? 'text-red-600' : 'text-green-600')
                  const val = v == null ? 'Variance' : `${sign}${fmtNum(v)}`
                  const centerClass = v == null ? 'center' : ''
                  return (
                    <input className={`cc-readonly variance-cell ${centerClass} ${cls}`} readOnly value={val} />
                  )
                })() : null}
              </div>
            </div>
          )
        })}

        {editingFormula && (
          <FormulaBuilderModal
            field={editingFormula}
            existingExpr={formulas[editingFormula] || ''}
            fieldsList={template.fields.map(f => f.key)}
            labels={Object.fromEntries(
              template.fields.map(f => [f.key, (f.label||f.key).replace(/\{currency\}/g, curr)])
            )}
            onSave={saveFormula}
            onCancel={() => setEditingFormula(null)}
          />
        )}

        {/* Dropdown Option Formula Modal */}
        {editingDropdownField && (
          <div className="fbm-overlay">
            <div className="fbm-modal">
              <h3>Edit formulas for dropdown options</h3>
              <table style={{ width: '100%', marginBottom: 16 }}>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Formula</th>
                  </tr>
                </thead>
                <tbody>
                  {dropdownOptionEdits.map((opt, i) => (
                    <tr key={opt.value}>
                      <td>{opt.label}</td>
                      <td>{opt.value}</td>
                      <td style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                          type="text"
                          value={opt.formula || ''}
                          readOnly
                          style={{ width: '70%' }}
                        />
                        <button
                          type="button"
                          className="formula-btn"
                          title="Edit formula with builder"
                          onClick={() => openOptionFormulaBuilder(i)}
                        >
                          ⚙️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="fbm-actions">
                <button type="button" onClick={saveDropdownOptionFormulas}>Save</button>
                <button type="button" onClick={() => { setEditingDropdownField(null); setDropdownOptionEdits([]); setEditingDropdownOptionIdx(null); }}>Cancel</button>
              </div>
              {/* FormulaBuilderModal for dropdown option */}
              {editingDropdownOptionIdx !== null && (
                <FormulaBuilderModal
                  field={dropdownOptionEdits[editingDropdownOptionIdx].label}
                  existingExpr={dropdownOptionEdits[editingDropdownOptionIdx].formula || ''}
                  fieldsList={template.fields.map(f => f.key)}
                  labels={Object.fromEntries(
                    template.fields.map(f => [f.key, (f.label||f.key).replace(/\{currency\}/g, curr)])
                  )}
                  onSave={saveOptionFormula}
                  onCancel={() => setEditingDropdownOptionIdx(null)}
                />
              )}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit">Save</button>
          <button type="button" onClick={() => navigate('/costcards', { replace:true })}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
